import * as React from "react"
import { cn } from "../../lib/utils"

type HeaderBg = 'default' | 'gradient' | 'none'
type Elevation = 'none' | 'sm' | 'md' | 'lg'

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    variant?: 'default' | 'stat' | 'elevated' | 'ghost'
    // Legacy props for backward compatibility
    title?: string
    subtitle?: string
    headerBg?: HeaderBg
    headerAction?: React.ReactNode
    icon?: React.ReactNode
    elevation?: Elevation
    border?: boolean
  }
>(({ className, variant = 'default', title, subtitle, headerBg, headerAction, icon, elevation, border, children, ...props }, ref) => {
  const variants = {
    default: "rounded-xl border bg-card text-card-foreground shadow-sm hover:shadow-md transition-shadow duration-200",
    stat: "rounded-xl border bg-card text-card-foreground shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-200",
    elevated: "rounded-xl border-0 bg-card text-card-foreground shadow-lg hover:shadow-xl transition-shadow duration-200",
    ghost: "rounded-xl bg-transparent text-card-foreground"
  }

  const headerBgClasses: Record<HeaderBg, string> = {
    default: 'bg-slate-50 border-b border-slate-200',
    gradient: 'bg-gradient-to-r from-orange-600 to-amber-500 text-white',
    none: ''
  }

  // Legacy elevation classes (for backward compatibility)
  const elevationClasses: Record<Elevation, string> = {
    none: '',
    sm: 'shadow-sm',
    md: 'shadow',
    lg: 'shadow-lg'
  }

  // Compute combined class including legacy props
  const computedClass = cn(
    variants[variant],
    elevation ? elevationClasses[elevation] : '',
    border === false ? 'border-0' : ''
  )
  
  // Legacy mode: if title is provided, render with legacy structure
  if (title) {
    const bgClass = headerBg ? headerBgClasses[headerBg] : headerBgClasses.default

  return (
      <div
        ref={ref}
        className={cn(computedClass, "overflow-hidden", className)}
        {...props}
      >
        {/* Legacy header */}
        <div className={cn("px-5 py-4 flex items-center justify-between", bgClass)}>
          <div className="flex items-center gap-3">
            {icon && <span className="text-current">{icon}</span>}
            <div>
              <h3 className={cn(
                "font-semibold text-base m-0",
                headerBg === 'gradient' ? 'text-white' : 'text-slate-900'
              )}>
                {title}
              </h3>
              {subtitle && (
                <p className={cn(
                  "m-0 text-sm",
                  headerBg === 'gradient' ? 'text-white/80' : 'text-slate-500'
                )}>
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          {headerAction && <div>{headerAction}</div>}
        </div>
      <div className="p-5">{children}</div>
      </div>
    )
  }
  
  // New mode: simple container
  return (
    <div
      ref={ref}
      className={cn(computedClass, className)}
      {...props}
    >
      {children}
    </div>
  )
})
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    compact?: boolean
  }
>(({ className, compact = false, ...props }, ref) => (
  <div 
    ref={ref} 
    className={cn(
      "flex flex-col space-y-1.5",
      compact ? "p-4" : "p-6",
      className
    )} 
    {...props} 
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement> & {
    size?: 'sm' | 'default' | 'lg'
  }
>(({ className, size = 'default', children, ...props }, ref) => {
  const sizes = {
    sm: "text-sm font-semibold leading-none tracking-tight",
    default: "text-base font-semibold leading-none tracking-tight",
    lg: "text-lg font-semibold leading-none tracking-tight"
  }
  
  return (
    // eslint-disable-next-line jsx-a11y/heading-has-content
    <h3
      ref={ref}
      className={cn(sizes[size], className)}
      {...props}
    >
      {children}
    </h3>
  )
})
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    compact?: boolean
  }
>(({ className, compact = false, ...props }, ref) => (
  <div 
    ref={ref} 
    className={cn(
      compact ? "p-4 pt-0" : "p-6 pt-0",
      className
    )} 
    {...props} 
  />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div 
    ref={ref} 
    className={cn("flex items-center p-6 pt-0", className)} 
    {...props} 
  />
))
CardFooter.displayName = "CardFooter"

// StatCard for metrics display - Orange theme with enhanced features
const StatCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    title: string
    value: string | number
    description?: string
    icon?: React.ReactNode
    trend?: {
      value: number
      isPositive: boolean
      label?: string
    }
    color?: 'default' | 'orange' | 'emerald' | 'amber' | 'rose' | 'blue'
    sparklineData?: number[]
    loading?: boolean
    onClick?: () => void
  }
>(({ className, title, value, description, icon, trend, color = 'default', sparklineData, loading, onClick, ...props }, ref) => {
  const colorClasses = {
    default: 'text-slate-900',
    orange: 'text-orange-600',
    emerald: 'text-emerald-600',
    amber: 'text-amber-600',
    rose: 'text-rose-600',
    blue: 'text-blue-600'
  }
  
  const iconBgClasses = {
    default: 'bg-slate-100',
    orange: 'bg-orange-100',
    emerald: 'bg-emerald-100',
    amber: 'bg-amber-100',
    rose: 'bg-rose-100',
    blue: 'bg-blue-100'
  }

  const sparklineColorClasses = {
    default: '#64748b',
    orange: '#f97316',
    emerald: '#10b981',
    amber: '#f59e0b',
    rose: '#f43f5e',
    blue: '#3b82f6'
  }
  
  // Loading skeleton
  if (loading) {
    return (
      <Card ref={ref} variant="stat" className={cn("relative overflow-hidden animate-pulse", className)} {...props}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="space-y-2 flex-1">
              <div className="h-3 w-24 bg-slate-200 rounded" />
              <div className="h-8 w-20 bg-slate-200 rounded" />
              <div className="h-3 w-32 bg-slate-200 rounded" />
            </div>
            <div className="h-11 w-11 bg-slate-200 rounded-lg" />
          </div>
        </CardContent>
      </Card>
    )
  }
  
  return (
    <Card 
      ref={ref} 
      variant="stat" 
      className={cn(
        "relative overflow-hidden group",
        onClick && "cursor-pointer hover:shadow-lg hover:border-orange-200 transition-all",
        className
      )} 
      onClick={onClick}
      {...props}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="space-y-2 flex-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              {title}
            </p>
            <p className={cn("text-2xl font-bold tracking-tight tabular-nums", colorClasses[color])}>
              {typeof value === 'number' ? value.toLocaleString() : value}
            </p>
            {description && (
              <p className="text-xs text-muted-foreground">
                {description}
              </p>
            )}
            {trend && (
              <div className={cn(
                "flex items-center gap-1 text-xs font-medium",
                trend.isPositive ? "text-emerald-600" : "text-rose-600"
              )}>
                <svg 
                  className={cn("h-3 w-3", !trend.isPositive && "rotate-180")} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
                </svg>
                <span>{Math.abs(trend.value)}%</span>
                {trend.label && <span className="text-slate-500 ml-1">{trend.label}</span>}
              </div>
            )}
          </div>
          {icon && (
            <div className={cn("rounded-lg p-2.5 transition-transform group-hover:scale-110", iconBgClasses[color])}>
              {icon}
            </div>
          )}
        </div>
        
        {/* Sparkline chart */}
        {sparklineData && sparklineData.length > 0 && (
          <div className="h-10 -mx-2 -mb-2 mt-2 overflow-hidden">
            <svg 
              className="w-full h-full" 
              viewBox={`0 0 ${sparklineData.length * 10} 40`}
              preserveAspectRatio="none"
            >
              <path
                d={`M ${sparklineData.map((value, i) => {
                  const x = i * (sparklineData.length > 1 ? (sparklineData.length * 10) / (sparklineData.length - 1) : 0);
                  const maxVal = Math.max(...sparklineData);
                  const minVal = Math.min(...sparklineData);
                  const range = maxVal - minVal || 1;
                  const y = 35 - ((value - minVal) / range) * 30;
                  return `${x},${y}`;
                }).join(' L ')}`}
                fill="none"
                stroke={sparklineColorClasses[color]}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                opacity="0.5"
                className="transition-opacity group-hover:opacity-100"
              />
            </svg>
          </div>
        )}
      </CardContent>
    </Card>
  )
})
StatCard.displayName = "StatCard"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent, StatCard }
